import { isEqual } from 'lodash';
import { EVENT_MODES, SORT_LABELS, STATUS_TYPES, URL_PATH } from '../../constants';
import { IEvent, IEventValidationModel } from '../../models';
import { TranslateHook } from '../../modules/schema-generator/Utils';
import { IEventFilterState } from '../../store/reducer/events/EventFilterReducer';

/**
 * @method isQueryParamSortExist
 * @description To get getSortValue
 * @param queryParam
 * @returns
 */
export const isQueryParamSortExist = (queryParam: { [key: string]: string }) => queryParam.sort === '' || queryParam.sort === undefined;
export const getSortValue = (queryParam: { [key: string]: string }) => {
  if (isQueryParamSortExist(queryParam)) {
    return '';
  }
  return queryParam.sort;
};

/**
 * @method getTypeValue
 * @description Retrieves the value of the 'type' query parameter from the given object.
 * If the 'type' query parameter is not present or if it is empty, an empty string is returned.
 * @param queryParam - An object containing query parameters.
 * @returns The value of the 'type' query parameter, or an empty string if it is not present or empty.
 */
export const getTypeValue = (queryParam: { [key: string]: string }) => {
  if (isQueryParamSortExist(queryParam)) {
    return '';
  }
  return queryParam.type;
};

/**
 * @method getValue
 * @description To get getStatus or search text Value
 * @param status
 * @returns
*/
export const getValue = (item: string) => {
  if (item) {
    return item;
  }
  return '';
};

/**
 * @method getDateValue
 * @description To get DateValue
 * @param date
 * @returns
 */
export const getDateValue = (date: any) => {
  if (date) {
    return date;
  }
  return '';
};

/**
 * @method arrayBufferToString
 * @description Converts an array buffer to a string.
 * @param exportedPrivateKey - The array buffer to convert.
 * @returns The converted string.
 */
export const arrayBufferToString = (exportedPrivateKey: any) => {
  const byteArray = new Uint8Array(exportedPrivateKey);
  let byteString = '';
  byteArray.forEach((data) => {
    byteString += String.fromCodePoint(data);
  });
  return byteString;
};

const isLastModifiedEqual = (queryParam: { [key: string]: string }) => queryParam.sort === SORT_LABELS.LASTMODIFIED;

const isStatusEqual = (queryParam: { [key: string]: string }) => queryParam.sort === SORT_LABELS.STATUS;

const isEventNameEqual = (queryParam: { [key: string]: string }) => queryParam.sort === SORT_LABELS.EVENTNAME;

/**
 * @method getSortStateCondition
 * @description To get sortState - sortStateCondition
 */
const getSortStateCondition = (queryParam: { [key: string]: string }) => {
  if (isLastModifiedEqual(queryParam)) {
    return `${queryParam.sort},${queryParam.type}`;
  }
  if (isEventNameEqual(queryParam)) {
    return `${queryParam.sort},${queryParam.type}`;
  }
  return 'updatedTimestamp,DESC';
};

/**
 * @method getSortState
 * @description To get status - sortState
 */
export const getSortState = (queryParam: { [key: string]: string }) => {
  if (isStatusEqual(queryParam)) {
    return `${queryParam.sort},${queryParam.type}`;
  }
  return getSortStateCondition(queryParam);
};

export const exportConfigConst = {
  contentDisposition_text: 'Content-Disposition',
  download_text: 'download'
};

export const headerTitleValue = (isLoading: boolean, eventDetails: IEvent[]) => {
  if (!isLoading) {
    return eventDetails[0].eventName !== '' ? eventDetails[0].eventName : '';
  }
  return '';
};

export const headerTitleStyling = (isLoading: boolean, styleClass: string) => (!isLoading ? styleClass : '');

export const titleBodyVersionText = (isLoading: boolean, eventDetails: IEvent[]) => {
  if (!isLoading) {
    return eventDetails[0].version !== '' ? eventDetails[0].version : '';
  }
  return '';
};

export const headerValueCreateTitle = (isLoading: boolean, eventDetails: IEvent[], isCreateClicked: boolean, preview: boolean, isEditFormClicked: boolean, text: string) => {
  if (!isLoading) {
    return !isCreateClicked && (preview || isEditFormClicked) ? eventDetails[0].eventName : text;
  }
  return '';
};

/**
 * @method shouldShowNavigationAwayPopup
 * @description displays the navigation away popup
 * @param eventMode event mode
 * @param isEventUpdateCalled isUpdateServiceSuccess
 * @param isActiveUpdate isActiveUpdate
 * @returns true if condition true else false
 */
export const shouldShowNavigationAwayPopup = (eventMode: string | undefined, isEventUpdateCalled: boolean, isActiveUpdate: boolean) =>
  (eventMode === EVENT_MODES.EDIT && ((!isEventUpdateCalled && !isActiveUpdate)));

/**
 * @method isEventDataValid
 * @description verifies the event data valid or not
 * @param eventItem data from get event api
 * @param eventData data from create event reducer
 * @returns true if valid, else false
 */
export const isEventDataValid = (eventItem: IEvent, eventData: IEvent) => ((eventItem?.eventName && eventItem?.eventSchemaId && eventItem?.eventType && eventItem?.schema)
  || (eventData?.eventName && eventData?.eventSchemaId && eventData?.eventType && eventData?.schema));

/**
 * @method getEventDetailsValidationError
 * @description To get event details validation error
 * @param translate translate function
 */
export const getEventDetailsValidationError = (translate: TranslateHook) => ({
  existingEventName: translate('event_validation_error.txt_existing_name'),
  eventVerificationIssue: translate('event_validation_error.txt_varification_issue'),
  eventName: translate('event_validation_error.txt_event_name'),
});

/**
 * @method getEventDetailsHelperText
 * @description To get event details helper text
 * @param translate translate function
 */
export const getEventDetailsHelperText = (translate: TranslateHook) => ({
  event_name_ht: translate('helper_text.txt_input_search_name'),
  desc_ht: translate('helper_text.txt_desc_ht'),
});

/**
 * @method isEventNameInputDisabled
 * @description Verifies the name input field is disabled or not
 * @param isEditFormClicked
 * @param cloneEventName
 * @param apiResponseStatus
 * @returns true if disabled, else false
 */
export const isEventNameInputDisabled = (isEditFormClicked: boolean, cloneEventName: string, apiResponseStatus: boolean) => (isEditFormClicked && !cloneEventName.length) || apiResponseStatus;

/**
 * @method getEventFormFieldsName
 * @description To get event form fields name
 * @param formData event form data
 */
export const getEventFormFieldsName = (formData: IEventValidationModel) => [formData.eventName.name, formData.description.name, formData.avroSchema.name, formData.schemaName.name, formData.eventType.name];

export const getEventFormInputFieldsName = (formData: IEventValidationModel) => [formData.eventName.name, formData.description.name, formData.avroSchema.name, formData.schemaName.name];

/**
 * @method checkFormValues
 * @description Checks if all form values are truthy.
 * @param formValues - An array of form values to check.
 * @returns A boolean indicating whether all form values are truthy.
 */
export const checkFormValues = (formValues: string[]) => !formValues.every(Boolean);

/**
 * @method checkFormErrors
 * @description Checks if there are any form errors.
 * @param formErrors - An array of boolean or undefined values representing form errors.
 * @returns A boolean value indicating whether there are any form errors.
 */
export const checkFormErrors = (formErrors: (boolean | undefined)[]) =>
  !formErrors.every(Boolean);

/**
 * @method checkShouldUpdateEnable
 * @description Checks whether the component should be updated or not.
 * @returns {boolean} True if the component should be updated, false otherwise.
 */
export const checkShouldUpdateEnable = (
  eventDetails: IEvent[],
  eventData: IEvent,
  isEditFormClicked: boolean,
  formData: IEventValidationModel,
  cloneEventName: string | undefined,
  sensitiveFieldsArr: string[]
): boolean => {
  const [eventItem] = eventDetails;
  if ((!formData.schemaName.isValid && isEditFormClicked)) {
    return true;
  }
  if (isEventDataValid(eventItem, eventData)) {
    const schemaDetailsItem = eventData.schema || eventItem.schema;
    // Data from reducer/event list
    const clonedEventData = {
      eventName: cloneEventName?.length ? '' : eventData.eventName || eventItem.eventName,
      eventDescription: eventData.eventDescription || eventItem.eventDescription,
      eventType: eventData.eventType || eventItem.eventType,
      schemaName: eventData.eventSchemaId || eventItem?.eventSchemaId,
      schema: JSON.parse(schemaDetailsItem),
      sensitiveFields: eventItem.sensitiveFields?.length ? eventItem.sensitiveFields : eventData.sensitiveFields || []
    };
    // Data in form
    const currentFormData = {
      eventName: formData.eventName.value,
      eventDescription: formData.description.value,
      eventType: formData.eventType.value,
      schemaName: formData.schemaName.value,
      schema: JSON.parse(formData.avroSchema.value),
      sensitiveFields: sensitiveFieldsArr
    };
    const isDataEqual = isEqual(currentFormData, clonedEventData);
    return isDataEqual;
  }
  return false;
};

/**
* @method isCreateFlow
* @description Checks if the current route pathname includes '/events/createEvent'.
* @returns {boolean} True if the current route pathname includes '/events/createEvent', false otherwise.
*/
export const isCreateFlow = (pathName: string): boolean => pathName.includes('/events/createEvent');

export interface EventFormNavigationState {
  from?: string;
  path?: string | { path?: string };
  creatreNewEventButtonClicked?: boolean;
}

/**
 * Resolves the destination when leaving the create/edit event form.
 * Avoids building invalid URLs such as `/control-plane/eventsundefined`.
 */
export const resolveEventFormBackPath = (locationState: unknown): string => {
  const queryParam = (locationState ?? null) as EventFormNavigationState | null;

  if (queryParam?.from) {
    return queryParam.from;
  }

  if (queryParam?.creatreNewEventButtonClicked) {
    return URL_PATH.DASHBOARD;
  }

  if (queryParam?.path && typeof queryParam.path === 'object' && queryParam.path.path) {
    const nestedPath = queryParam.path.path;
    return nestedPath.startsWith('/') ? nestedPath : `${URL_PATH.EVENT_HOME}${nestedPath}`;
  }

  if (typeof queryParam?.path === 'string') {
    if (queryParam.path.startsWith('/')) {
      return queryParam.path;
    }
    return `${URL_PATH.EVENT_HOME}${queryParam.path}`;
  }

  return URL_PATH.EVENT_HOME;
};

/**
 * Allowed exit paths for the create-event navigation guard.
 */
export const getCreateEventExitPaths = (
  locationState: unknown,
  createEventPath = `${URL_PATH.EVENT_HOME}${URL_PATH.CREATE_EVENT}`
): string[] => {
  const backPath = resolveEventFormBackPath(locationState);
  return backPath === createEventPath ? [backPath] : [backPath, createEventPath];
};

/**
 * Contains constant value for the default event list status.
 */
export const defaultEventListStatus = `${STATUS_TYPES.ACTIVE},${STATUS_TYPES.DRAFT},${STATUS_TYPES.PENDING_APPROVAL},${STATUS_TYPES.RETURNED}`;
export const defaultEventListStatusArray: string[] = [
  STATUS_TYPES.ACTIVE,
  STATUS_TYPES.DRAFT,
  STATUS_TYPES.PENDING_APPROVAL,
  STATUS_TYPES.RETURNED
];

/**
 * @method getSearchParams
 * @description Gets the Search Params
 * @param search : search param string
 * @returns params
 */
export const getSearchParams = (search: string) => {
  let params = {
    status: '',
    page: ''
  };
  try {
    params = JSON.parse(
      `{"${decodeURIComponent(
        search.replace(/&/g, '","').replace(/=/g, '":"')
      )}"}`
    );
  } catch {
    /* empty */
  }
  if (!params.status && !params.page) {
    params.status = defaultEventListStatus;
  }

  return params;
};

export const parseSortString = (val?: string) => {
  if (!val) return { sort: '', type: '' };
  const [sort, type] = val.split(',');
  return { sort: sort ?? '', type: type ?? '' };
};

/**
 *
 * @method getFilterParams
 * @description Gets the current filter parameters.
 * If the search url is present, it will return the search url parameters.
 * Otherwise, it will return the current filters from the redux store.
 * @param search - The search url
 * @param filterState - The current filter state
 * @returns The filter parameters
 */
export const getFilterParams = (search: string, filterState: IEventFilterState) => {
  const params = new URLSearchParams(search);
  const parsedParams = {
    status: params.get('status') ?? '',
    page: params.get('page') ?? '',
    item: params.get('item') ?? '',
    eventName: params.get('eventName') ?? '',
    fromDate: params.get('fromDate') ?? '',
    toDate: params.get('toDate') ?? '',
    sort: params.get('sort') ?? '',
    type: params.get('type') ?? ''
  };

  const hasUrlFilter = parsedParams.status || parsedParams.eventName || parsedParams.fromDate || parsedParams.toDate || parsedParams.sort || parsedParams.type;
  if (hasUrlFilter) {
    return parsedParams;
  }

  const { sort, type } = parseSortString(filterState.sort);
  return {
    status: filterState.status ?? '',
    page: params.get('page') ?? '',
    item: params.get('item') ?? '',
    eventName: filterState.searchText ?? '',
    fromDate: String(filterState.fromDate ?? ''),
    toDate: String(filterState.toDate ?? ''),
    sort,
    type
  };
};
